'use client';

import React, { useEffect, useState, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISSED_KEY = 'naija-pwa-install-dismissed';
const DISMISSED_EXPIRY_DAYS = 14;

function isDismissedRecently(): boolean {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    const days = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return days < DISMISSED_EXPIRY_DAYS;
  } catch {
    return false;
  }
}

function markDismissed() {
  try {
    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
  } catch {
    // ignore
  }
}

/**
 * PWAInstallPrompt — Phase 24
 *
 * - Shows a subtle install banner when the browser fires beforeinstallprompt
 * - On iOS Safari (where the event is unavailable), shows Share → Add to Home Screen instructions
 * - Respects dismissal: won't re-show for 14 days after user dismisses
 * - Never shows intrusive popups
 */
export default function PWAInstallPrompt() {
  const [mounted, setMounted] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    setMounted(true);

    // Detect standalone mode (already installed)
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      ('standalone' in window.navigator && (window.navigator as { standalone?: boolean }).standalone === true);
    setIsStandalone(standalone);

    if (standalone) return; // Already installed — don't show anything
    if (isDismissedRecently()) return;

    // Detect iOS Safari
    const ua = navigator.userAgent;
    const ios = /iphone|ipad|ipod/i.test(ua) && !/crios|fxios/i.test(ua);
    setIsIOS(ios);

    if (ios) {
      // Show iOS instructions after a short delay
      const t = setTimeout(() => setShowBanner(true), 3000);
      return () => clearTimeout(t);
    }

    // Listen for Chrome/Android install prompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'dismissed') markDismissed();
    setDeferredPrompt(null);
    setShowBanner(false);
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    markDismissed();
    setShowBanner(false);
  }, []);

  if (!mounted || !showBanner || isStandalone) return null;

  return (
    <div
      role="complementary"
      aria-label="Install Naija Explorer"
      className="fixed bottom-0 left-0 right-0 z-[150] px-4 pb-4"
      style={{ paddingBottom: 'max(16px, calc(env(safe-area-inset-bottom, 0px) + 16px))' }}
    >
      <div
        className="max-w-sm mx-auto rounded-2xl shadow-xl border border-border overflow-hidden"
        style={{ background: '#FFFFFF' }}
      >
        {/* Green accent top bar */}
        <div className="h-1 flex">
          <div className="flex-1" style={{ background: '#1B6B3A' }} />
          <div className="flex-1" style={{ background: '#FDF6E3' }} />
          <div className="flex-1" style={{ background: '#1B6B3A' }} />
        </div>

        <div className="px-4 py-3">
          <div className="flex items-start gap-3">
            {/* Logo */}
            <div
              className="w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center"
              style={{ background: 'rgba(27,107,58,0.1)' }}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/assets/images/2ECEA112-99E4-485E-9851-0D0355803F63-1787869789071.png"
                alt="Naija Explorer"
                width={28}
                height={28}
                style={{ objectFit: 'contain' }}
              />
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-foreground leading-tight">
                Install Naija Explorer
              </p>

              {isIOS ? (
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                  Tap{' '}
                  <span className="font-semibold" aria-label="Share button">
                    Share ↑
                  </span>{' '}
                  then{' '}
                  <span className="font-semibold">Add to Home Screen</span> to install.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                  Add to your home screen for the best experience.
                </p>
              )}
            </div>

            {/* Dismiss */}
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-all min-h-[36px] min-w-[36px] flex items-center justify-center"
              aria-label="Dismiss install prompt"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Install button — only for non-iOS */}
          {!isIOS && deferredPrompt && (
            <button
              onClick={handleInstall}
              className="mt-3 w-full rounded-xl py-2.5 text-sm font-semibold text-white transition-all min-h-[44px]"
              style={{ background: '#1B6B3A' }}
            >
              Install Naija Explorer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
